import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
import { recentPurchases } from '../data/recentPurchases';

/**
 * Off by default: the list in data/recentPurchases.ts is sample data.
 * Set to true once it is fed by real orders.
 */
const ENABLED = false;
const FIRST_DELAY = 6000;
const VISIBLE_MS = 5500;
const GAP_MS = 12000;

/**
 * Low-frequency "someone just bought this" notification, bottom-left so it
 * never covers the CTA. Dismissable, and it stops for the session once closed.
 */
export function PurchaseNotifications() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!ENABLED || dismissed) return;
    let timeout: ReturnType<typeof setTimeout>;
    const show = () => {
      setVisible(true);
      timeout = setTimeout(hide, VISIBLE_MS);
    };
    const hide = () => {
      setVisible(false);
      timeout = setTimeout(() => {
        setIndex(i => (i + 1) % recentPurchases.length);
        show();
      }, GAP_MS);
    };
    timeout = setTimeout(show, FIRST_DELAY);
    return () => clearTimeout(timeout);
  }, [dismissed]);
  if (!ENABLED || dismissed) return null;
  const purchase = recentPurchases[index];
  const minutes = 2 + index * 7 % 26;
  return <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)] sm:bottom-6 sm:left-6" data-id="element-250" data-mp="1cinui76w2l2w">
      <AnimatePresence data-id="element-251" data-mp="12kgogje51nfo">
        {visible && <motion.div key={index} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 14,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto flex w-[320px] max-w-full items-start gap-3 rounded-2xl border border-cream-dark bg-white p-3.5 shadow-card" data-id="element-252" data-mp="12j6otprn076x">
            <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gold/25" data-id="element-253" data-mp="3atehv3x0uf73">
              <CheckIcon className="h-4 w-4 text-gold-deep" aria-hidden="true" data-id="element-254" data-mp="3fqoh1ba7q584" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-255" data-mp="31yglfmuo0kb0">
              <p className="text-[13px] leading-snug text-ink" data-id="element-256" data-mp="36r9tokpcearu">
                <span className="font-semibold" data-id="element-257" data-mp="2s2594wv2rugk">{purchase.name}</span> in{' '}
                {purchase.city}, {purchase.state} just got Memory Care Activity
                Ready
              </p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-258" data-mp="2j1fe3vdn283w">
                {minutes} minutes ago
              </p>
            </div>
            <button type="button" onClick={() => setDismissed(true)} aria-label="Dismiss purchase notifications" className="-mr-1 -mt-1 flex h-7 w-7 flex-none items-center justify-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-cream hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-259" data-mp="2n290gkbfneuo">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-260" data-mp="28ig0uccla0fd" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}